/**
 * Doc fee — a flat fee some dealerships add to every bike they sell (e.g. $499).
 *
 * Configured once per dealership under Targets (the `docFee` key on the
 * front-end targets blob) and applied at report time, so turning it on or
 * changing the amount restates every period containing unit sales.
 *
 * New and used are configured independently: a dealership can charge on new
 * only, used only, or both, at different amounts. Anything that isn't a new or
 * used unit never carries a fee.
 *
 * It counts as back-end (F&I) gross, not unit gross: the fee lands in F&I
 * totals and leaves sales price and cost — and therefore unit GP — untouched.
 * Reserve and product breakdowns also stay pure; the doc fee is a third addend
 * alongside them.
 *
 * Mirrors backend/src/utils/docFee.ts. The scoreboards aggregate activities
 * client-side while Profit Summary and Overview aggregate server-side, so the
 * two implementations must stay in step.
 */

const OFF = { enabled: false, amount: 0 };
const DISABLED = { enabled: false, new: OFF, used: OFF };

/**
 * Normalize one tier. A tier flagged on but with no usable amount is treated as
 * off, so a half-filled setting can't quietly add $0 rows or NaN its way into a
 * total.
 */
function readTier(raw) {
  if (!raw || !raw.enabled) return OFF;

  const amount = Number(raw.amount);
  if (!isFinite(amount) || amount <= 0) return OFF;

  return { enabled: true, amount };
}

/**
 * Read the doc fee out of a front-end targets object. The returned `enabled`
 * flag is true when either tier is on.
 *
 * Settings saved before used bikes were supported carry the flat
 * `{ enabled, amount }` shape; those were new-only, so they're read that way.
 */
export function getDocFee(targets) {
  const raw = targets && targets.docFee;
  if (!raw) return DISABLED;

  const legacy = raw.new === undefined && raw.used === undefined;
  const newTier = readTier(legacy ? raw : raw.new);
  const usedTier = legacy ? OFF : readTier(raw.used);

  return {
    enabled: newTier.enabled || usedTier.enabled,
    new: newTier,
    used: usedTier
  };
}

/** New/used is stored inconsistently across import paths, so compare loosely. */
export function unitKind(activity) {
  const value = String((activity && activity.motorcycleNewUsed) || '').toUpperCase();
  if (value === 'NEW') return 'new';
  if (value === 'USED') return 'used';
  return null;
}

export function isNewUnit(activity) {
  return unitKind(activity) === 'new';
}

/** Per-activity doc fee, for pipelines that handle one deal at a time. */
export function docFeeFor(activity, setting) {
  if (!setting || !setting.enabled) return 0;

  const kind = unitKind(activity);
  if (!kind) return 0;

  const tier = setting[kind];
  return tier.enabled ? tier.amount : 0;
}

/** Total doc fee income for a set of sales activities. */
export function docFeeIncome(activities, setting) {
  if (!setting || !setting.enabled) return 0;
  return (activities || []).reduce((sum, a) => sum + docFeeFor(a, setting), 0);
}
